import { useState, useEffect, useRef } from 'react';
import { useT } from '../../i18n';
import { Spinner } from '../ui/Spinner';

export interface WalletCardProps {
  address: string;
  onChange: (value: string) => void;
  placeholder: string;
  isBuy: boolean;
  error?: string;
  validating: boolean;
  disabled: boolean;
  onFocus: () => void;
  onBlur: () => void;
  focused?: boolean;
}

export function WalletCard({
  address,
  onChange,
  placeholder,
  error,
  validating,
  disabled,
  onFocus,
  onBlur,
  focused,
}: WalletCardProps) {
  const t = useT();
  const showInlineSpinner = validating && address.trim().length >= 10;
  const [shaking, setShaking] = useState(false);
  const prevErrorRef = useRef(error);

  useEffect(() => {
    if (error && !prevErrorRef.current) {
      setShaking(true);
      const timer = setTimeout(() => setShaking(false), 400);
      return () => clearTimeout(timer);
    }
    prevErrorRef.current = error;
  }, [error]);

  return (
    <div>
      <div
        className={`flex flex-col gap-[4px] p-4 card-hover${shaking ? ' error-shake' : ''}`}
        style={{
          background: 'var(--t-surface)',
          borderRadius: 'var(--t-card-radius)',
        }}
      >
        <div className="text-[13px]" style={{ color: 'var(--t-text-secondary)', letterSpacing: '0.13px', lineHeight: '20px' }}>
          {t('wallet.label')}
        </div>
        <div className="relative flex items-center">
          <input
            type="text"
            value={address}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            spellCheck={false}
            autoComplete="off"
            className="w-full bg-transparent text-[13px] outline-none"
            style={{
              color: 'var(--t-text)',
              border: 'none',
              padding: 0,
              paddingRight: showInlineSpinner ? 24 : 0,
              fontSize: 13,
              lineHeight: '20px',
              letterSpacing: '0.13px',
              backgroundColor: 'transparent',
            }}
            disabled={disabled}
          />
          {/* Inline validation spinner */}
          {showInlineSpinner && (
            <div className="absolute right-0 top-0 bottom-0 flex items-center">
              <Spinner size={14} />
            </div>
          )}
          {/* Cursor blink indicator for empty active state */}
          {!address && focused && !disabled && (
            <div
              className="absolute left-0 top-0 bottom-0 flex items-center"
              style={{ pointerEvents: 'none' }}
            >
              <div
                style={{
                  width: 1,
                  height: 16,
                  background: 'var(--t-text)',
                  animation: 'fadeIn 0.5s ease-in-out infinite alternate',
                }}
              />
            </div>
          )}
        </div>
      </div>
      {error && (
        <div className="text-[12px] mt-1.5 text-center" style={{ color: 'var(--t-error)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
